/**
 * Risk Assessment UI thresholds and visual symbol system.
 *
 * Two AI plans (Claude / GPT) are considered "agreeing" when both their
 * Risk per share % and R:R ratio differ by less than these tolerances.
 * Below: display single (averaged) values. Above: dual "Claude/GPT"
 * comparison with ⚠️.
 */
export const RISK_AGREEMENT_TOLERANCE = {
  riskPctDelta: 1.0,   // pp — Risk per share % delta
  rrDelta: 0.2,        // R:R ratio delta
};

/**
 * Total selected risk vs account size thresholds. Drives the Filter chip
 * row's "Total risk" indicator color (gray / yellow / red).
 *
 * Day trading standard: ≤2% per-trade, 5% daily cap. The "selected total"
 * approximates "if all positions stop simultaneously".
 */
export const TOTAL_RISK_THRESHOLDS = {
  warningPct: 2.0,   // gray → yellow at this %
  severePct: 5.0,    // yellow → red at this %
};

/**
 * Score number color gradient. 1-3 LOW (green), 4-6 MEDIUM (yellow),
 * 7-10 HIGH (red). Used in Summary chip row + Risk Assessment table Score
 * column.
 */
export const RISK_SCORE_COLOR = (score) =>
  score <= 3 ? '#16a34a' :
  score <= 6 ? '#ca8a04' :
               '#dc2626';

/**
 * R:R (reward-to-risk) thresholds. < 1 fails the day-trader minimum,
 * 1-2 is acceptable, ≥ 2 is the "standard good" target.
 */
export const RR_THRESHOLDS = {
  poor: 1.0,    // 🔴 below
  good: 2.0,    // 🟢 at or above
};

/**
 * Per-share Loss % thresholds (entry-to-stop distance as % of entry).
 * < 2% is tight (whipsaw risk), 2-4% is the standard day-trade band,
 * 4-7% is wide, > 7% is overly wide.
 */
export const LOSS_PCT_THRESHOLDS = {
  tight: 2.0,    // 🟡 below (too tight, whipsaw)
  safe: 4.0,     // 🟢 between tight..safe
  wide: 7.0,     // 🟡 between safe..wide
                 // 🔴 above wide
};

/** Relative Volume (today's vol / avg). ⚡ shown when ≥ this multiplier. */
export const RVOL_HIGH_THRESHOLD = 2.5;

/**
 * RVOL color tiers (separate from ⚡ momentum bonus).
 * < 1x = below average (🔴), 1-1.5 = average (🟡), 1.5+ = good (🟢).
 */
export const RVOL_TIERS = {
  low: 1.0,     // 🔴 below
  good: 1.5,    // 🟢 at or above
};

/**
 * Gap % double-threshold: 5% triggers yellow ⚠️ ("large", gap fill risk),
 * 10% triggers red 🚨 ("extreme", commonly fills before continuing).
 */
export const GAP_THRESHOLDS = {
  warningPct: 5.0,
  extremePct: 10.0,
};

/**
 * Stop's distance from VWAP, in %. < 1% triggers yellow ⚠️ ("whipsaw"
 * risk: stop near a key intraday technical level). ≥ 3% triggers green ✓
 * ("safe distance" — stop unlikely to be hit by normal noise).
 */
export const STOP_VWAP_THRESHOLDS = {
  whipsawPct: 1.0,
  safePct: 3.0,
};

/** Position $ per-stock dropdown options. User typically trades $80-110k. */
export const POSITION_DOLLARS_OPTIONS = [50000, 80000, 100000, 110000];
export const DEFAULT_POSITION_DOLLARS = 100000;
export const DEFAULT_ACCOUNT_SIZE = 220000;

/**
 * Sector name abbreviations for the Risk Assessment table. Polygon's SIC
 * descriptions are long ("Information Technology Services") — abbreviate
 * to fit a narrow column. Fallback: first 7 chars when unmatched.
 */
export const SECTOR_ABBREV = {
  'Technology': 'Tech',
  'Information Technology Services': 'IT Svcs',
  'Healthcare': 'Health',
  'Energy': 'Energy',
  'Financial Services': 'Finance',
  'Industrials': 'Indust',
  'Consumer Cyclical': 'CCyc',
  'Consumer Defensive': 'CDef',
  'Materials': 'Materials',
  'Utilities': 'Utils',
  'Real Estate': 'RealEst',
  'Communication Services': 'Comm',
};
